"use client"

import { ReactNode } from "react"
import { useTranslations } from "next-intl"
import { Icon, MapPinIcon, ShareIcon, Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import { FavoriteButton } from "@/features/hospital/ui/molecules"
import { toast } from "@/shared/utils/toastMessage"

interface HospitalHeroActionsProps {
  name: string
  tel: string
  fullAddress: string
  favorite: boolean
  closed?: boolean
  suspended?: boolean
}

const ActionLabel = ({ children }: { children: ReactNode }) => (
  <Typography as="span" size={13} lineHeight={18} className="font-medium text-gray-700">
    {children}
  </Typography>
)

/**
 * 병원을 정한 사람이 바로 하는 일 — 전화·길찾기·공유·저장 — 을 히어로 바로 아래 모은다.
 * 개편 전에는 저장만 화면 맨 아래 고정 바에 있었고, 전화·주소는 본문 중간의 텍스트라
 * 스크롤을 되짚어야 눌 수 있었다.
 *
 * 타일 모양은 `hospital-hero-action`(globals.css) 하나로 정의해 저장 버튼까지 같은 판을 쓴다.
 */
const HospitalHeroActions = ({ name, tel, fullAddress, favorite, closed = false, suspended = false }: HospitalHeroActionsProps) => {
  const t = useTranslations()
  const isNonOperating = closed || suspended
  const mapQuery = encodeURIComponent(`${name} ${fullAddress}`.trim())

  const handleShare = async () => {
    const url = window.location.href

    // 모바일 브라우저는 OS 공유 시트를 띄우고, 없는 환경(데스크톱 등)은 링크를 복사한다.
    if (navigator.share) {
      try {
        await navigator.share({ title: name, url })
        return
      } catch (error) {
        // 공유 시트를 닫은 것은 실패가 아니다 — 복사로 대체하지 않는다.
        if ((error as { name?: string })?.name === "AbortError") return
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      toast.success(t("common.copyLinkSuccess"))
    } catch {
      toast.error(t("common.copyLinkFailed"))
    }
  }

  return (
    <div className="flex items-stretch gap-2">
      {!isNonOperating && tel ? (
        <a
          href={`tel:${tel}`}
          className="hospital-hero-action"
          aria-label={`${name} ${t("hero.call")}`}
        >
          <Icon src="/icons/call.svg" alt="" width={16} height={16} />
          <ActionLabel>{t("hero.call")}</ActionLabel>
        </a>
      ) : null}
      {!isNonOperating ? (
        <a
          href={`https://map.naver.com/p/search/${mapQuery}`}
          target="_blank"
          rel="noreferrer noopener"
          className="hospital-hero-action"
        >
          <MapPinIcon size={16} className="text-gray-500" />
          <ActionLabel>{t("hero.directions")}</ActionLabel>
        </a>
      ) : null}
      <CustomButton className="hospital-hero-action" onClick={handleShare}>
        <ShareIcon size={16} />
        <ActionLabel>{t("hero.share")}</ActionLabel>
      </CustomButton>
      <FavoriteButton favorite={favorite} variant="tile" />
    </div>
  )
}

export default HospitalHeroActions
